import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Status } from "../../../../common/constants";
import { pendingDeletionsUrl, forceDeleteDeviceUrl } from "../../../../config";
import api from "../../../../api";
import {
  IPendingDeletionsResponse,
  IPendingDeletionsState,
} from "../../../../common/types/devicesTypes";

const GET_PENDING_DELETIONS = "getPendingDeletions";
const FORCE_DELETE_DEVICE = "forceDeleteDevice";

const initialState: IPendingDeletionsState = {
  pending: [],
  forceDeleteStatus: null,
  status: Status.IDLE,
  error: null,
};

export const fetchPendingDeletions = createAsyncThunk(
  GET_PENDING_DELETIONS,
  async () => {
    const response = await api.get<IPendingDeletionsResponse>(pendingDeletionsUrl);
    return response.data;
  }
);

export const forceDeleteDevice = createAsyncThunk(
  FORCE_DELETE_DEVICE,
  async (deviceId: string, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `${forceDeleteDeviceUrl}?deviceId=${encodeURIComponent(deviceId)}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Could not remove the device. Please try again."
      );
    }
  }
);

const pendingDeletionsSlice = createSlice({
  name: GET_PENDING_DELETIONS,
  initialState,
  reducers: {
    clearPendingDeletions: (state) => {
      state.pending = [];
      state.status = Status.IDLE;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPendingDeletions.pending, (state) => {
        state.status = Status.PENDING;
      })
      .addCase(fetchPendingDeletions.fulfilled, (state, action) => {
        state.status = Status.SUCCESS;
        state.pending = action.payload?.values ?? [];
      })
      .addCase(fetchPendingDeletions.rejected, (state, action) => {
        state.status = Status.FAILED;
        state.error = action.error?.message ?? null;
        state.pending = [];
      })
      .addCase(forceDeleteDevice.pending, (state) => {
        state.forceDeleteStatus = Status.PENDING;
      })
      .addCase(forceDeleteDevice.fulfilled, (state, action) => {
        state.forceDeleteStatus = Status.SUCCESS;
        const removedId = action.meta.arg;
        state.pending = state.pending.filter((device) => device.deviceId !== removedId);
      })
      .addCase(forceDeleteDevice.rejected, (state, action) => {
        state.forceDeleteStatus = Status.FAILED;
        state.error = (action.payload as string) ?? null;
      });
  },
});

export const { clearPendingDeletions } = pendingDeletionsSlice.actions;
export default pendingDeletionsSlice.reducer;
